/**
 * Canonical FE training state. Maps every backend (/status DB enum) and
 * Socket.IO status string to one of these, so progress logic has a single
 * vocabulary. Unknown non-empty strings map to 'running' (keep polling) so a
 * new/unmapped backend phase can never be misread as a terminal state.
 */
export type FeTrainingState = 'running' | 'completed' | 'failed' | 'not_found' | 'idle';

const COMPLETED = new Set(['completed', 'training_completed', 'model_training_completed']);
const FAILED = new Set(['failed', 'abandoned', 'training_failed', 'error', 'failed_storage', 'data_validation_error']);
const RUNNING = new Set(['running', 'in_progress', 'training_in_progress', 'processing', 'training_starting', 'training_started']);

export function normalizeTrainingStatus(raw: string | null | undefined): FeTrainingState {
  if (!raw) return 'not_found';
  if (COMPLETED.has(raw)) return 'completed';
  if (FAILED.has(raw)) return 'failed';
  if (raw === 'not_found') return 'not_found';
  if (raw === 'idle') return 'idle';
  if (RUNNING.has(raw)) return 'running';
  // Robustness guard for free-form terminal failures the backend may write
  // without a DB check constraint (training_results.status). Catch any
  // unmapped status whose text signals a failure.
  const lowered = raw.toLowerCase();
  if (lowered.includes('fail') || lowered.includes('error')) return 'failed';
  return 'running'; // unknown non-empty → still running; never a false terminal
}
